import { ReactNode } from 'react'
import { Box, Tooltip } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import type { Add } from '@mui/icons-material'
import type { ActionAccessInfo } from './utils'

type ActionIconIndicatorProps = {
  Icon: typeof Add
  access: ActionAccessInfo | undefined
  iconSx?: SxProps<Theme>
  muted?: boolean
  tooltip?: ReactNode
  policyNames?: readonly string[]
}

const ActionIconIndicator = ({
  Icon,
  access,
  iconSx,
  muted = false,
  tooltip,
  policyNames,
}: ActionIconIndicatorProps) => {
  if (!access || access.total === 0)
    return <Box sx={{ width: 20, height: 20, display: 'inline-flex', verticalAlign: 'middle' }} />
  const iconColor = muted ? 'text.disabled' : 'text.primary'
  const showCount = !(access.total === 1 && access.all === 1)
  const iconStyles = iconSx
    ? Array.isArray(iconSx)
      ? [{ fontSize: 18, color: iconColor }, ...iconSx]
      : [{ fontSize: 18, color: iconColor }, iconSx]
    : { fontSize: 18, color: iconColor }

  const indicator = (
    <Box
      sx={{
        position: 'relative',
        width: 20,
        height: 20,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        verticalAlign: 'middle',
      }}
    >
      <Icon sx={iconStyles} />
      {policyNames && policyNames.length > 0 ? (
        <Box
          component="span"
          sx={{
            position: 'absolute',
            left: -3,
            top: -2,
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: 'secondary.main',
          }}
        />
      ) : null}
      {showCount ? (
        <Box
          component="span"
          sx={{
            position: 'absolute',
            right: -3,
            top: -2,
            bgcolor: muted ? 'grey.500' : 'error.main',
            color: 'common.white',
            fontSize: 8,
            fontWeight: 700,
            lineHeight: 1,
            width: 12,
            height: 12,
            borderRadius: '50%',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {access.total}
        </Box>
      ) : null}
    </Box>
  )

  if (!tooltip) return indicator
  return (
    <Tooltip title={tooltip} arrow placement="top">
      {indicator}
    </Tooltip>
  )
}

export default ActionIconIndicator
